import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useOutletContext, useNavigate } from 'react-router-dom';
import {
  Building2, Users, DollarSign, TrendingUp, ShoppingBag,
  ArrowUpRight, ArrowDownRight, RefreshCw, Activity, Globe,
  ChevronRight, AlertCircle, CheckCircle, Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';

import { API } from '@/config/api';
import { CA_BASE } from '@/utils/getBasePath';
const ca = (route = '') => route ? `${CA_BASE}/${route}` : (CA_BASE || '/');

// Stat Card Component
function StatCard({ title, value, change, changeType, icon: Icon, color, onClick }) {
  const isPositive = changeType === 'positive';
  
  return (
    <Card 
      className="bg-slate-800 border-slate-700 cursor-pointer hover:bg-slate-750 transition-colors" 
      onClick={onClick}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-400">{title}</p>
            <p className="text-3xl font-bold text-white mt-2">{value}</p>
            {change && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                {isPositive ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                <span>{change}</span>
                <span className="text-slate-500">vs last month</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-xl ${color}`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Restaurant Row
function RestaurantRow({ restaurant, onClick }) {
  const statusColors = {
    active: 'bg-green-500/20 text-green-400',
    inactive: 'bg-slate-500/20 text-slate-400',
    pending: 'bg-yellow-500/20 text-yellow-400'
  };

  return (
    <div 
      className="flex items-center justify-between py-3 px-4 hover:bg-slate-700/50 rounded-lg cursor-pointer transition-colors"
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold text-sm">
          {restaurant.name?.charAt(0) || 'R'}
        </div>
        <div>
          <p className="font-medium text-white">{restaurant.name}</p>
          <p className="text-sm text-slate-400">{restaurant.city}, {restaurant.state}</p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="text-right">
          <p className="font-semibold text-white">${(restaurant.total_revenue || 0).toFixed(2)}</p>
          <p className="text-xs text-slate-400">Total revenue</p>
        </div>
        <Badge className={statusColors[restaurant.status] || statusColors.active}>
          {restaurant.status || 'active'}
        </Badge>
        <ChevronRight className="h-4 w-4 text-slate-500" />
      </div>
    </div>
  );
}

export default function CompanyDashboard() {
  const navigate = useNavigate();
  const { token, admin } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(true);

  const getHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  const fetchDashboardData = useLatestCallback(async () => {
    setLoadError(false);
    try {
      const [statsRes, restaurantsRes] = await Promise.all([
        axios.get(`${API}/company-admin/stats`, getHeaders()),
        axios.get(`${API}/company-admin/restaurants`, getHeaders())
      ]);
      setStats(statsRes.data || null);
      setRestaurants(Array.isArray(restaurantsRes.data) ? restaurantsRes.data : []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Could not load dashboard data');
      setStats(null);
      setRestaurants([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    if (token) {
      fetchDashboardData();
    }
  }, [token, fetchDashboardData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-4">
          <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
          <p className="text-slate-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const statusCount = (status) => restaurants.filter(r => (r.status || 'active') === status).length;
  const statusPct = (status) => restaurants.length ? (statusCount(status) / restaurants.length) * 100 : 0;

  return (
    <div className="space-y-6 p-4 sm:p-6 dark rounded-2xl bg-slate-950 text-slate-100" data-testid="company-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Company Dashboard</h1>
          <p className="text-slate-400">Overview of your Chain29 platform</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
            onClick={fetchDashboardData}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button 
            size="sm"
            className="bg-gradient-to-r from-yellow-500 to-orange-500"
            onClick={() => navigate(ca('restaurants'))}
          >
            <Building2 className="h-4 w-4 mr-2" />
            Add Restaurant
          </Button>
        </div>
      </div>

      {loadError && (
        <Card className="bg-slate-800 border-slate-700" data-testid="company-dashboard-error">
          <CardContent className="p-4 flex items-center justify-between gap-3">
            <p className="text-sm text-red-400">Could not load dashboard data.</p>
            <Button variant="outline" size="sm" className="border-slate-600 text-slate-300" onClick={fetchDashboardData}>Try again</Button>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Restaurants"
          value={stats?.total_restaurants || 0}
          icon={Building2}
          color="bg-gradient-to-r from-blue-500 to-cyan-500"
          onClick={() => navigate(ca('restaurants'))}
        />
        <StatCard
          title="Active Users"
          value={stats?.total_users || 0}
          icon={Users}
          color="bg-gradient-to-r from-purple-500 to-pink-500"
          onClick={() => navigate(ca('users'))}
        />
        <StatCard
          title="Total Revenue"
          value={`$${(stats?.total_revenue || 0).toLocaleString()}`}
          icon={DollarSign}
          color="bg-gradient-to-r from-green-500 to-emerald-500"
          onClick={() => navigate(ca('transactions'))}
        />
        <StatCard
          title="Total Orders"
          value={(stats?.total_orders || 0).toLocaleString()}
          icon={ShoppingBag}
          color="bg-gradient-to-r from-orange-500 to-red-500"
          onClick={() => navigate(ca('analytics'))}
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Restaurants List - Takes 2 columns */}
        <Card className="lg:col-span-3 bg-slate-800 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className="text-lg font-semibold text-white">Restaurants</CardTitle>
              <CardDescription className="text-slate-400">Top performing locations</CardDescription>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate(ca('restaurants'))}
              className="text-yellow-500 hover:text-yellow-400 hover:bg-slate-700"
            >
              View All
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-slate-700">
              {restaurants.length === 0 && (
                <p className="py-6 text-center text-sm text-slate-400">No restaurants yet</p>
              )}
              {restaurants.slice(0, 5).map((restaurant) => (
                <RestaurantRow 
                  key={restaurant.id} 
                  restaurant={restaurant}
                  onClick={() => navigate(`${ca('restaurants')}?id=${restaurant.id}`)}
                />
              ))}
            </div>
          </CardContent>
        </Card>

      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Restaurant Status */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-400">Restaurant Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-400" />
                  <span className="text-sm text-slate-300">Active</span>
                </div>
                <span className="font-semibold text-white">{statusCount('active')}</span>
              </div>
              <Progress value={statusPct('active')} className="h-2 bg-slate-700 [&>div]:bg-green-500" />
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-yellow-400" />
                  <span className="text-sm text-slate-300">Pending</span>
                </div>
                <span className="font-semibold text-white">{statusCount('pending')}</span>
              </div>
              <Progress value={statusPct('pending')} className="h-2 bg-slate-700 [&>div]:bg-yellow-500" />
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-400" />
                  <span className="text-sm text-slate-300">Inactive</span>
                </div>
                <span className="font-semibold text-white">{statusCount('inactive')}</span>
              </div>
              <Progress value={statusPct('inactive')} className="h-2 bg-slate-700 [&>div]:bg-red-500" />
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-400">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="h-16 flex-col gap-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                onClick={() => navigate(ca('restaurants'))}
              >
                <Building2 className="h-5 w-5" />
                <span className="text-xs">Add Restaurant</span>
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="h-16 flex-col gap-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                onClick={() => navigate(ca('users'))}
              >
                <Users className="h-5 w-5" />
                <span className="text-xs">Add User</span>
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="h-16 flex-col gap-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                onClick={() => navigate(ca('analytics'))}
              >
                <TrendingUp className="h-5 w-5" />
                <span className="text-xs">View Reports</span>
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="h-16 flex-col gap-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                onClick={() => navigate(ca('settings'))}
              >
                <Globe className="h-5 w-5" />
                <span className="text-xs">Settings</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
